import { ArrowRight, Eye, EyeOff, Lock, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/routes/routeConstants';
import FormCheckbox from '@/shared/components/forms/FormCheckbox';
import FormInput from '@/shared/components/forms/FormInput';
import { handleApiError } from '@/shared/utils/errorUtils';
import { clearAllStorage } from '@/shared/utils/storageUtils';
import { tokenStorage } from '@/shared/utils/tokenStorage';

import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';

interface IFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const loginFieldClass =
  '!bg-white !border !border-gray-200 !text-gray-900 placeholder:!text-gray-400 !rounded-lg !py-3 !text-sm !shadow-none focus:!ring-2 focus:!ring-theme-500 focus:!border-theme-500';

const LoginScreen: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [hasClearedStorage, setHasClearedStorage] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const token = tokenStorage.getAccessToken();
    if (!isAuthenticated && !isLoading && !token && !hasClearedStorage) {
      clearAllStorage();
      setHasClearedStorage(true);
    }
  }, [isAuthenticated, isLoading, hasClearedStorage]);

  const onSubmit = async (formData: IFormData) => {
    setApiError('');
    setIsLoading(true);
    setHasClearedStorage(true);
    try {
      const response = await authService.login({
        username: formData.email,
        password: formData.password,
      });
      if (response && response.access && response.user) {
        await login(response.access, response.user, response.refresh);
      } else {
        setApiError('Invalid response from server. Please try again.');
        setHasClearedStorage(false);
      }
    } catch (error) {
      setHasClearedStorage(false);
      handleApiError(error, 'Login failed. Please check your credentials and try again.');

      if (error && typeof error === 'object' && 'message' in error) {
        const errorMessage = error.message as string;
        if (
          errorMessage.toLowerCase().includes('invalid credentials') ||
          errorMessage.toLowerCase().includes('authentication failed')
        ) {
          setApiError('Invalid email or password. Please check your credentials and try again.');
        } else if (
          errorMessage.toLowerCase().includes('network') ||
          errorMessage.toLowerCase().includes('connection')
        ) {
          setApiError('Network error. Please check your internet connection and try again.');
        } else if (
          errorMessage.toLowerCase().includes('server') ||
          errorMessage.toLowerCase().includes('500')
        ) {
          setApiError('Server error. Please try again later or contact support.');
        } else {
          setApiError(errorMessage);
        }
      } else {
        setApiError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {};

  return (
    <div
      className='relative min-h-screen w-screen overflow-y-auto font-sans'
      style={{
        overscrollBehavior: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <img
        src='/Background_Admin.webp'
        alt=''
        className='absolute inset-0 h-full w-full object-fill'
        aria-hidden
      />

      <div className='relative z-10 mx-auto grid min-h-screen w-full max-w-7xl grid-cols-1 content-start md:grid-cols-2 md:content-normal md:px-8 lg:px-12'>
        {/* Tall vertical divider — nearly full viewport height */}
        <div
          className='pointer-events-none absolute left-1/2 top-10 hidden w-px -translate-x-1/2 bg-gray-300 md:block'
          style={{ height: 'calc(100% - 5rem)' }}
          aria-hidden
        />

        {/* Left branding — vertically centered */}
        <div className='flex flex-col items-center justify-center px-4 pb-6 pt-10 md:items-start md:px-6 md:py-10 md:pr-14 lg:pr-16'>
          <div>
            <img
              src='/Humit-WhiteBg.png'
              alt='Humit'
              className='h-14 w-auto max-w-full object-contain md:h-[4.5rem]'
            />

            <div className='mt-5 flex max-w-[18rem] items-center gap-3 md:mt-6 md:max-w-[22rem]'>
              <span className='h-px flex-1 bg-gray-300' />
              <span className='shrink-0 text-[11px] font-semibold tracking-[0.22em] text-theme-500'>
                ADMIN PORTAL
              </span>
              <span className='h-px flex-1 bg-gray-300' />
            </div>

            <div className='mt-10 flex w-[18rem] flex-col items-center md:mt-12 md:w-[22rem]'>
              <p className='mb-2 text-center text-xs text-gray-500'>Powered by</p>
              <img
                src='/logo_novarroh.avif'
                alt='Novarroh'
                className='h-12 object-contain md:h-14'
              />
            </div>
          </div>
        </div>

        {/* Right login card — slightly tighter on mobile only; desktop unchanged */}
        <div className='mt-6 flex items-center justify-center px-4 py-4 md:mt-4 md:justify-end md:pl-28 md:pr-6 md:py-6 lg:pl-32 lg:pr-8'>
          <div
            className='w-full max-w-md shrink-0 rounded-2xl border border-gray-100 bg-white px-6 py-8 shadow-xl shadow-gray-200/60 md:px-8 md:py-10'
            style={{ touchAction: 'pan-y' }}
          >
            <div className='mb-8 text-center md:mb-10'>
              <h1 className='text-2xl font-semibold text-gray-900 md:text-3xl'>Admin Login</h1>
            </div>

            {apiError ? (
              <div className='mb-5 rounded-lg border border-red-200 bg-red-50 p-3'>
                <p className='text-center text-sm text-red-600'>{apiError}</p>
              </div>
            ) : null}

            <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 md:space-y-5'>
              <FormInput
                name='email'
                type='email'
                placeholder='Username'
                register={register}
                errors={errors}
                showRequiredMark={false}
                icon={User}
                validation={{
                  required: 'Username is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address',
                  },
                }}
                className={`${loginFieldClass} ${
                  errors.email ? '!border-red-400 !bg-red-50' : 'hover:!border-gray-300'
                }`}
                containerClassName='[&_label]:hidden [&_p]:text-red-600 [&_svg]:text-gray-400'
              />

              <FormInput
                name='password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Password'
                register={register}
                errors={errors}
                showRequiredMark={false}
                icon={Lock}
                validation={{
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Password must be at least 6 characters' },
                }}
                className={`${loginFieldClass} ${
                  errors.password ? '!border-red-400 !bg-red-50' : 'hover:!border-gray-300'
                }`}
                containerClassName='[&_label]:hidden [&_p]:text-red-600 [&_svg]:text-gray-400'
                rightSlot={
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='p-0.5 text-gray-400 transition-colors hover:text-gray-600'
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                  </button>
                }
              />

              <div className='flex items-center justify-between'>
                <Controller
                  name='rememberMe'
                  control={control}
                  render={({ field }) => (
                    <FormCheckbox
                      id='rememberMe'
                      name={field.name}
                      checked={!!field.value}
                      onChange={(checked) => field.onChange(checked)}
                      label='Remember me'
                      size='sm'
                      className='!flex-row items-center [&_input]:h-3.5 [&_input]:w-3.5 [&_input]:border-gray-300 [&_input]:bg-white [&_input]:text-theme-500 [&_input]:focus:ring-theme-500'
                      labelClassName='text-sm text-gray-600 !font-normal'
                    />
                  )}
                />
                <button
                  type='button'
                  onClick={handleForgotPassword}
                  className='text-sm font-medium text-theme-500 transition-colors hover:text-theme-600'
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type='submit'
                disabled={isLoading}
                className='flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-theme-600 to-emerald-400 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
              >
                {isLoading ? 'Logging in…' : 'Login'}
                {!isLoading ? <ArrowRight className='h-4 w-4' /> : null}
              </button>
            </form>
          </div>
        </div>
      </div>

      <p className='pointer-events-none absolute bottom-4 left-4 z-10 text-[11px] text-gray-500 md:bottom-6 md:left-6 md:text-xs'>
        Copyright © {new Date().getFullYear()}, Novarroh Technologies, Inc
      </p>
    </div>
  );
};

export default LoginScreen;
